import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import type {
  QualityInspection,
  QualityDefect,
  DefectType,
  InspectionType,
  InspectionResult,
  DefectSeverity,
} from '@/types/quality';

// ============== QUERIES ==============

export function useWorkOrderInspections(workOrderId: string) {
  return useQuery({
    queryKey: ['quality-inspections', workOrderId],
    queryFn: async () => {
      const { data, error } = await api
        .from('quality_inspections')
        .select(`
          *,
          inspector:profiles!quality_inspections_inspector_id_fkey(full_name),
          defects:quality_defects(*)
        `)
        .eq('work_order_id', workOrderId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as QualityInspection[];
    },
    enabled: !!workOrderId,
  });
}

export function useInspection(inspectionId: string) {
  return useQuery({
    queryKey: ['quality-inspection', inspectionId],
    queryFn: async () => {
      const { data, error } = await api
        .from('quality_inspections')
        .select(`
          *,
          inspector:profiles!quality_inspections_inspector_id_fkey(full_name),
          defects:quality_defects(*)
        `)
        .eq('id', inspectionId)
        .single();

      if (error) throw error;
      return data as QualityInspection;
    },
    enabled: !!inspectionId,
  });
}

export function useDefectTypes() {
  return useQuery({
    queryKey: ['defect-types'],
    queryFn: async () => {
      const { data, error } = await api
        .from('defect_types')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      return data as DefectType[];
    },
  });
}

// ============== MUTATIONS ==============

interface CreateInspectionData {
  work_order_id: string;
  process_record_id?: string;
  plybook_execution_id?: string;
  inspection_type?: InspectionType;
  inspection_point?: string;
  ply_number?: number;
  inspector_id: string;
  notes?: string;
}

export function useCreateInspection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreateInspectionData) => {
      const { data: result, error } = await api
        .from('quality_inspections')
        .insert([{
          ...data,
          status: 'in_progress',
          inspection_date: new Date().toISOString().split('T')[0],
        }])
        .select()
        .single();

      if (error) throw error;
      return result as QualityInspection;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quality-inspections', variables.work_order_id] });
      toast({ title: 'Inspección iniciada' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al crear inspección',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

interface CompleteInspectionData {
  id: string;
  work_order_id: string;
  result: InspectionResult;
  notes?: string;
  requires_rework?: boolean;
  rework_description?: string;
  corrective_action?: string;
}

export function useCompleteInspection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CompleteInspectionData) => {
      const { id, work_order_id, ...updateData } = data;

      const { error } = await api
        .from('quality_inspections')
        .update({
          ...updateData,
          status: 'completed',
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quality-inspections', variables.work_order_id] });
      queryClient.invalidateQueries({ queryKey: ['quality-inspection', variables.id] });
      toast({
        title: 'Inspección completada',
        description: variables.result === 'approved' ? 'Aprobada' : 'Revisar resultado',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al completar inspección',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

interface AddDefectData {
  inspection_id: string;
  work_order_id: string;
  defect_type: string;
  severity: DefectSeverity;
  location?: string;
  description?: string;
  photo_url?: string;
}

export function useAddDefect() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: AddDefectData) => {
      const { work_order_id, ...defectData } = data;

      // Insert defect
      const { data: defect, error } = await api
        .from('quality_defects')
        .insert([defectData])
        .select()
        .single();

      if (error) throw error;

      // Update inspection counts
      const { error: updateError } = await api
        .from('quality_inspections')
        .update({
          has_defects: true,
          defect_count: await getDefectCount(data.inspection_id),
          updated_at: new Date().toISOString(),
        })
        .eq('id', data.inspection_id);

      if (updateError) throw updateError;

      return defect as QualityDefect;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quality-inspections', variables.work_order_id] });
      queryClient.invalidateQueries({ queryKey: ['quality-inspection', variables.inspection_id] });
      toast({ title: 'Defecto registrado' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al registrar defecto',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

async function getDefectCount(inspectionId: string): Promise<number> {
  const { count } = await api
    .from('quality_defects')
    .select('*', { count: 'exact', head: true })
    .eq('inspection_id', inspectionId);
  return count || 0;
}

interface ResolveDefectData {
  id: string;
  inspection_id: string;
  work_order_id: string;
  resolution_notes?: string;
  resolved_by: string;
}

export function useResolveDefect() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: ResolveDefectData) => {
      const { id, inspection_id, work_order_id, ...updateData } = data;

      const { error } = await api
        .from('quality_defects')
        .update({
          ...updateData,
          is_resolved: true,
          resolved_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quality-inspections', variables.work_order_id] });
      queryClient.invalidateQueries({ queryKey: ['quality-inspection', variables.inspection_id] });
      toast({ title: 'Defecto resuelto' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al resolver defecto',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
